import React, { useState, useEffect } from 'react';
import { 
  Card, 
  Upload, 
  Button, 
  Alert, 
  Progress, 
  List, 
  Typography, 
  Space,
  Tag,
  Divider,
  message,
  Table,
  Modal,
  Input,
  Checkbox,
  Spin,
  Tooltip,
  Row,
  Col
} from 'antd';
import { 
  CloudUploadOutlined, 
  FileExcelOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DownloadOutlined,
  InfoCircleOutlined,
  DeleteOutlined,
  EyeOutlined,
  WarningOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import { 
  parseCSVData, 
  bulkUploadSites, 
  getSitesByOrganization, 
  exportSitesToCSV, 
  replaceAllSites 
} from '../../services/siteService';
import * as XLSX from 'xlsx';
import { useAuth } from '../../contexts/AuthContext';

const { Title, Text, Paragraph } = Typography;
const { Dragger } = Upload;

/**
 * SiteUpload - Component for uploading organizational sites during onboarding
 * Super Admin Only - Now with current sites display and replace functionality
 */
const SiteUpload = ({ orgId, onSitesUploaded, orgName }) => {
  const { currentUser } = useAuth();
  
  // Existing state
  const [uploading, setUploading] = useState(false);
  const [uploadResults, setUploadResults] = useState(null);
  const [parsedData, setParsedData] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // New state for current sites functionality
  const [currentSites, setCurrentSites] = useState([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [replaceMode, setReplaceMode] = useState(false);
  const [replaceConfirmModal, setReplaceConfirmModal] = useState(false);
  const [replaceConfirmStep, setReplaceConfirmStep] = useState(1);
  const [replaceConfirmText, setReplaceConfirmText] = useState('');
  const [exportedBeforeReplace, setExportedBeforeReplace] = useState(false);
  const [replacingAllSites, setReplacingAllSites] = useState(false);

  // Required columns for site upload (exact order as specified)
  const SITE_REQUIRED_COLUMNS = [
    'country',
    'state',
    'suburb',
    'site_name',
    'site_type',
    'address_line_1',
    'address_line_2',
    'postcode',
    'region',
    'status',
    'site_manager_name',
    'site_manager_mobile',
    'site_manager_email'
  ];

  // Site types - dynamic per organization (no fixed list)

  // Load current sites when component mounts or orgId changes
  useEffect(() => {
    if (orgId) {
      loadCurrentSites();
    }
  }, [orgId]);

  // Load current sites from database
  const loadCurrentSites = async () => {
    if (!orgId) return;
    
    setLoadingSites(true);
    try {
      const sites = await getSitesByOrganization(orgId);
      setCurrentSites(sites);
      console.log(`Loaded ${sites.length} current sites for organization ${orgId}`);
    } catch (error) {
      console.error('Error loading current sites:', error);
      message.error('Failed to load current sites');
      setCurrentSites([]);
    } finally {
      setLoadingSites(false);
    }
  };

  // Export current sites to CSV
  const handleExportSites = () => {
    if (currentSites.length === 0) {
      message.warning('No sites to export');
      return;
    }
    
    try {
      const filename = exportSitesToCSV(currentSites, orgName || 'Organization');
      message.success(`Exported ${currentSites.length} sites to ${filename}`);
      setExportedBeforeReplace(true);
    } catch (error) {
      console.error('Error exporting sites:', error);
      message.error('Failed to export sites');
    }
  };

  // Handle file upload (supports both CSV and Excel)
  const handleFileUpload = (file) => {
    const isExcel = file.name.toLowerCase().endsWith('.xlsx') || file.name.toLowerCase().endsWith('.xls');
    
    if (isExcel) {
      // Handle Excel files
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          // Get first worksheet
          const firstSheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[firstSheetName];
          
          // Convert to CSV format
          const csvContent = XLSX.utils.sheet_to_csv(worksheet);
          
          // Parse using existing CSV parser
          const sites = parseCSVData(csvContent);
          setParsedData(sites);
          message.success(`Successfully parsed ${sites.length} sites from Excel file`);
        } catch (error) {
          console.error('Error parsing Excel file:', error);
          message.error(`Failed to parse Excel file: ${error.message}`);
          setParsedData(null);
        }
      };
      
      reader.readAsArrayBuffer(file);
    } else {
      // Handle CSV files
    const reader = new FileReader();
    
    reader.onload = (e) => {
      try {
        const csvContent = e.target.result;
        const sites = parseCSVData(csvContent);
        setParsedData(sites);
          message.success(`Successfully parsed ${sites.length} sites from CSV file`);
      } catch (error) {
        console.error('Error parsing CSV:', error);
          message.error(`Failed to parse CSV file: ${error.message}`);
        setParsedData(null);
      }
    };
    
    reader.readAsText(file);
    }
    
    return false; // Prevent default upload
  };

  // Upload sites to database (existing functionality)
  const handleUploadSites = async () => {
    if (!parsedData || !orgId) {
      message.error('No data to upload or organization not selected');
      return;
    }

    setUploading(true);
    setUploadProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 200);

      const results = await bulkUploadSites(parsedData, orgId);
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResults(results);
      
      if (results.successful > 0) {
        message.success(`Successfully uploaded ${results.successful} sites!`);
        if (onSitesUploaded) {
          onSitesUploaded(results);
        }
        // Refresh current sites display
        loadCurrentSites();
      }
      
      if (results.failed > 0) {
        message.warning(`${results.failed} sites had errors. Check the results below.`);
      }

    } catch (error) {
      console.error('Error uploading sites:', error);
      message.error('Failed to upload sites');
    } finally {
      setUploading(false);
    }
  };

  // Replace all sites workflow
  const handleReplaceAllSites = async () => {
    if (!parsedData || parsedData.length === 0) {
      message.error('Please upload a CSV file with new sites first');
      return;
    }
    
    if (!orgId) {
      message.error('Organization not selected');
      return;
    }

    setReplacingAllSites(true);
    setUploadProgress(0);

    try {
      // Simulate progress updates
      const progressInterval = setInterval(() => {
        setUploadProgress(prev => Math.min(prev + 10, 90));
      }, 300);

      const results = await replaceAllSites(
        orgId, 
        orgName || 'Organization', 
        parsedData, 
        currentUser.uid
      );
      
      clearInterval(progressInterval);
      setUploadProgress(100);
      setUploadResults(results);
      
      if (results.success) {
        message.success(`Successfully replaced all sites! Deleted: ${results.deleted}, Uploaded: ${results.uploaded}`);
        if (onSitesUploaded) {
          onSitesUploaded(results);
        }
        // Refresh current sites display
        loadCurrentSites();
      } else {
        message.warning(`Site replacement completed with ${results.errors.length} errors`);
      }

      // Reset replace mode
      setReplaceMode(false);
      setReplaceConfirmModal(false);
      setReplaceConfirmStep(1);
      setReplaceConfirmText('');
      setExportedBeforeReplace(false);

    } catch (error) {
      console.error('Error replacing sites:', error);
      message.error('Failed to replace sites');
    } finally {
      setReplacingAllSites(false);
    }
  };

  // Start replace all workflow
  const startReplaceAllWorkflow = () => {
    if (currentSites.length === 0) {
      message.warning('No existing sites to replace');
      return;
    }
    
    // Check if we have new data to upload
    if (!parsedData || parsedData.length === 0) {
      message.warning('Please upload a CSV file first before replacing sites');
      return;
    }
    
    setReplaceMode(true);
    setReplaceConfirmModal(true);
    setReplaceConfirmStep(1);
    setReplaceConfirmText('');
    setExportedBeforeReplace(false);
  };

  // Handle replace confirmation steps
  const handleReplaceConfirmNext = () => {
    if (replaceConfirmStep === 1) {
      setReplaceConfirmStep(2);
    } else if (replaceConfirmStep === 2) {
      if (replaceConfirmText !== 'REPLACE ALL') {
        message.error('Please type "REPLACE ALL" to confirm');
        return;
      }
      if (!exportedBeforeReplace) {
        message.error('Please export current sites before proceeding');
        return;
      }
      
      setReplaceConfirmModal(false);
      handleReplaceAllSites();
    }
  };

  // Cancel replace workflow
  const cancelReplaceWorkflow = () => {
    setReplaceMode(false);
    setReplaceConfirmModal(false);
    setReplaceConfirmStep(1);
    setReplaceConfirmText('');
    setExportedBeforeReplace(false);
  };

  // Download error report (existing functionality)
  const downloadErrorReport = () => {
    if (!uploadResults || uploadResults.errors.length === 0) return;

    const errorData = uploadResults.errors.map(error => ({
      row: error.row,
      error: error.error,
      data: JSON.stringify(error.data)
    }));

    const csv = [
      'Row,Error,Data',
      ...errorData.map(row => `${row.row},"${row.error}","${row.data}"`)
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `site_upload_errors_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Download template CSV with exact column order
  const downloadTemplate = () => {
    const headers = SITE_REQUIRED_COLUMNS.join(',');
    const sampleData = [
      'Australia,NSW,Sydney,Bupa Clinic Sydney,clinic,123 George Street,Level 2,2000,APAC,active,John Smith,+61400123456,john.smith@example.com',
      'Australia,VIC,Melbourne,Automated Storage,warehouse,456 Industrial Drive,,3000,APAC,active,,,',
      'Australia,NT,Alice Springs,Remote Health Clinic,clinic,789 Outback Road,,0870,Remote Areas,active,Mike Wilson,+61400555666,mike.wilson@example.com'
    ].join('\n');
    
    const csv = `${headers}\n${sampleData}`;

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'site_upload_template.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  // Table columns for current sites display
  const currentSitesColumns = [
    {
      title: 'Site Name',
      dataIndex: 'site_name',
      key: 'site_name',
      width: 200,
      ellipsis: true
    },
    {
      title: 'Type',
      dataIndex: 'site_type',
      key: 'site_type',
      width: 100,
      render: (type) => (
        <Tag color="blue" size="small">
          {type}
        </Tag>
      )
    },
    {
      title: 'Address',
      dataIndex: 'address_line_1',
      key: 'address_line_1',
      width: 200,
      ellipsis: true
    },
    {
      title: 'Suburb',
      dataIndex: 'suburb',
      key: 'suburb',
      width: 120
    },
    {
      title: 'State',
      dataIndex: 'state',
      key: 'state',
      width: 80
    },
    {
      title: 'Manager',
      dataIndex: 'site_manager_name',
      key: 'site_manager_name',
      width: 150,
      ellipsis: true,
      render: (name) => name || <span style={{ color: '#999', fontStyle: 'italic' }}>No manager</span>
    },
    {
      title: 'Mobile',
      dataIndex: 'site_manager_mobile',
      key: 'site_manager_mobile',
      width: 130,
      render: (mobile) => mobile || <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
    },
    {
      title: 'Latitude',
      dataIndex: 'latitude',
      key: 'latitude',
      width: 100,
      render: (lat) => lat ? Number(lat).toFixed(6) : 'N/A'
    },
    {
      title: 'Longitude',
      dataIndex: 'longitude',
      key: 'longitude',
      width: 100,
      render: (lng) => lng ? Number(lng).toFixed(6) : 'N/A'
    }
  ];

  // Check if user has permission
  if (!currentUser?.isSuperAdmin) {
    return (
      <Alert
        message="Access Denied"
        description="Site upload is only available to super administrators."
        type="error"
        showIcon
      />
    );
  }

  return (
    <div className="site-upload">
      <Title level={4}>
        <CloudUploadOutlined className="me-2" />
        Site Management
      </Title>
      
      <Paragraph type="secondary">
        View current sites, export site data, and upload new sites for the organization. 
        Supports complete site list replacement with audit logging.
      </Paragraph>

      <Alert
        message="Site Replacement Workflow"
        description="To replace all sites: (1) Export current sites as backup, (2) Upload new CSV or Excel file below, (3) Choose 'Replace All Sites' from the preview options."
        type="info"
        showIcon
        className="mb-3"
      />

      {/* Current Sites Display */}
      <Card 
        className="mb-3"
        title={
          <Space>
            <EyeOutlined />
            <span>Current Sites ({currentSites.length})</span>
          </Space>
        }
        extra={
          <Space>
            <Button 
              icon={<ReloadOutlined />}
              onClick={loadCurrentSites}
              loading={loadingSites}
              size="small"
            >
              Refresh
            </Button>
            <Button 
              icon={<DownloadOutlined />}
              onClick={handleExportSites}
              disabled={currentSites.length === 0}
              size="small"
            >
              Export CSV
            </Button>
            <Tooltip title="Upload a CSV file first to enable site replacement">
              <Button 
                icon={<DeleteOutlined />}
                onClick={startReplaceAllWorkflow}
                disabled={currentSites.length === 0 || !parsedData}
                danger
                size="small"
              >
                Replace All Sites
              </Button>
            </Tooltip>
          </Space>
        }
      >
        {loadingSites ? (
          <div className="text-center py-4">
            <Spin />
            <div className="mt-2">Loading sites...</div>
          </div>
        ) : currentSites.length === 0 ? (
          <Alert
            message="No sites found"
            description="This organization has no sites uploaded yet. Use the upload section below to add sites."
            type="info"
            showIcon
          />
        ) : (
          <Table
            dataSource={currentSites}
            columns={currentSitesColumns}
            pagination={false}
            scroll={{ x: 1200, y: 400 }}
            size="small"
            rowKey="id"
          />
        )}
      </Card>

      {/* Replace All Confirmation Modal */}
      <Modal
        title={
          <Space>
            <WarningOutlined style={{ color: '#ff4d4f' }} />
            <span>Replace All Sites - Step {replaceConfirmStep} of 2</span>
          </Space>
        }
        open={replaceConfirmModal}
        onCancel={cancelReplaceWorkflow}
        footer={[
          <Button key="cancel" onClick={cancelReplaceWorkflow}>
            Cancel
          </Button>,
          <Button 
            key="next" 
            type="primary" 
            danger={replaceConfirmStep === 2}
            onClick={handleReplaceConfirmNext}
          >
            {replaceConfirmStep === 1 ? 'Continue' : 'Replace All Sites'}
          </Button>
        ]}
      >
        {replaceConfirmStep === 1 ? (
          <div>
            <Alert
              message="Warning: This will delete all existing sites"
              description={`This action will permanently delete all ${currentSites.length} existing sites and replace them with the new upload. This cannot be undone.`}
              type="warning"
              showIcon
              className="mb-3"
            />
            <p><strong>Organization:</strong> {orgName || 'Current Organization'}</p>
            <p><strong>Sites to be deleted:</strong> {currentSites.length}</p>
            <p><strong>New sites to upload:</strong> {parsedData?.length || 0}</p>
          </div>
        ) : (
          <div>
            <Alert
              message="Final Confirmation Required"
              description="Please confirm you want to proceed with replacing all sites."
              type="error"
              showIcon
              className="mb-3"
            />
            
            <div className="mb-3">
              <Checkbox
                checked={exportedBeforeReplace}
                onChange={(e) => setExportedBeforeReplace(e.target.checked)}
              >
                I have exported the current sites as a backup
              </Checkbox>
            </div>
            
            <div className="mb-3">
              <Text strong>Type "REPLACE ALL" to confirm:</Text>
              <Input
                value={replaceConfirmText}
                onChange={(e) => setReplaceConfirmText(e.target.value)}
                placeholder="REPLACE ALL"
                className="mt-2"
              />
            </div>
            
            <Alert
              message="This action will be logged for audit purposes"
              type="info"
              showIcon
              size="small"
            />
          </div>
        )}
      </Modal>

      <Divider />

      {/* Instructions and Template */}
      <Card className="mb-3" size="small">
        <Space direction="vertical" style={{ width: '100%' }}>
          <div className="d-flex justify-content-between align-items-center">
            <Text strong>CSV File Requirements</Text>
            <Button 
              icon={<DownloadOutlined />}
              onClick={downloadTemplate}
              size="small"
            >
              Download CSV Template
            </Button>
          </div>
          
          <div>
            <Text strong>Required Columns (in exact order):</Text>
            <div className="mt-1">
              {SITE_REQUIRED_COLUMNS.map(col => (
                <Tag key={col} color="blue" size="small" className="mb-1">
                  {col}
                </Tag>
              ))}
            </div>
          </div>

          <div>
            <Text strong>Site Types:</Text>
            <div className="mt-1">
              <Alert
                message="Any site type accepted"
                description="You can use any site type text (e.g., clinic, office, warehouse, retail, etc.). New site types will be automatically registered for your organization and appear in the map legend."
                type="success"
                showIcon
                size="small"
              />
            </div>
          </div>

          <Alert
            message="Geocoding Note"
            description="If latitude/longitude columns are provided, they will be used. Otherwise, addresses will be geocoded automatically when Google Maps API is configured."
            type="info"
            showIcon
            size="small"
          />
          
          <Alert
            message="Excel File Support"
            description="Excel files (.xlsx, .xls) are supported. The first worksheet will be used, and column headers should match the CSV template format."
            type="info"
            showIcon
            size="small"
          />
          
          <Alert
            message="Manager Fields Optional"
            description="Site manager name, mobile, and email are now optional! Perfect for unmanned sites, automated facilities, or centrally managed locations. Only location details are required."
            type="success"
            showIcon
            size="small"
          />
        </Space>
      </Card>

      {/* File Upload Section */}
      {!parsedData && !uploadResults && (
        <Card title={replaceMode ? "Upload New Sites (Replace All)" : "Upload Sites"}>
          <Dragger
            name="sites"
            accept=".csv,.xlsx,.xls"
            beforeUpload={handleFileUpload}
            showUploadList={false}
            disabled={uploading || replacingAllSites}
          >
            <p className="ant-upload-drag-icon">
              <FileExcelOutlined style={{ fontSize: '48px', color: '#1890ff' }} />
            </p>
            <p className="ant-upload-text">
              Click or drag CSV/Excel file to upload
            </p>
            <p className="ant-upload-hint">
              Support for .csv, .xlsx, and .xls files
            </p>
          </Dragger>
        </Card>
      )}

      {/* Parsed Data Preview */}
      {parsedData && !uploadResults && (
        <Card 
          title={`Preview: ${parsedData.length} sites parsed`}
          extra={
            <Space>
              <Button onClick={() => {
                setParsedData(null);
                setReplaceMode(false);
              }}>
                Clear
              </Button>
              {replaceMode ? (
                <Button 
                  type="primary"
                  danger
                  icon={<DeleteOutlined />}
                  onClick={startReplaceAllWorkflow}
                  loading={replacingAllSites}
                  disabled={parsedData.length === 0}
                >
                  Replace All Sites
                </Button>
              ) : (
              <Button 
                type="primary"
                icon={<CloudUploadOutlined />}
                onClick={handleUploadSites}
                loading={uploading}
                disabled={parsedData.length === 0}
              >
                Upload Sites
              </Button>
              )}
            </Space>
          }
        >
          {(uploading || replacingAllSites) && (
            <div className="mb-3">
              <Text>{replacingAllSites ? 'Replacing all sites...' : 'Uploading sites...'}</Text>
              <Progress percent={uploadProgress} />
            </div>
          )}
          
          <Table
            dataSource={parsedData.slice(0, 5)}
            columns={[
              { title: 'Site Name', dataIndex: 'site_name', key: 'site_name' },
              { title: 'Type', dataIndex: 'site_type', key: 'site_type' },
              { title: 'Address', dataIndex: 'address_line_1', key: 'address_line_1' },
              { 
                title: 'Manager', 
                dataIndex: 'site_manager_name', 
                key: 'site_manager_name',
                render: (name) => name || <span style={{ color: '#999', fontStyle: 'italic' }}>No manager</span>
              },
              { 
                title: 'Mobile', 
                dataIndex: 'site_manager_mobile', 
                key: 'site_manager_mobile',
                render: (mobile) => mobile || <span style={{ color: '#999', fontStyle: 'italic' }}>N/A</span>
              }
            ]}
            pagination={false}
            size="small"
            scroll={{ x: true }}
          />
          
          {parsedData.length > 5 && (
            <Text type="secondary" className="mt-2">
              Showing first 5 of {parsedData.length} sites...
            </Text>
          )}
        </Card>
      )}

      {/* Upload Results */}
      {uploadResults && (
        <Card
          title={
            <Space>
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
              {replaceMode ? 'Site Replacement Complete' : 'Upload Complete'}
            </Space>
          }
          extra={
            <Button onClick={() => {
              setUploadResults(null);
              setParsedData(null);
              setUploadProgress(0);
              setReplaceMode(false);
            }}>
              {replaceMode ? 'Replace More Sites' : 'Upload More Sites'}
            </Button>
          }
        >
          <div className="mb-3">
            <Space size="large">
              <div>
                <Text strong>{uploadResults.processed || uploadResults.deleted + uploadResults.uploaded}</Text>
                <br />
                <Text type="secondary">Processed</Text>
              </div>
              <div>
                <Text strong style={{ color: '#52c41a' }}>
                  {uploadResults.successful || uploadResults.uploaded}
                </Text>
                <br />
                <Text type="secondary">
                  {replaceMode ? 'Uploaded' : 'Successful'}
                </Text>
              </div>
              {replaceMode && (
                <div>
                  <Text strong style={{ color: '#ff4d4f' }}>{uploadResults.deleted}</Text>
                  <br />
                  <Text type="secondary">Deleted</Text>
                </div>
              )}
              <div>
                <Text strong style={{ color: '#ff4d4f' }}>
                  {uploadResults.errors?.length || 0}
                </Text>
                <br />
                <Text type="secondary">Errors</Text>
              </div>
            </Space>
          </div>

          {uploadResults.errors && uploadResults.errors.length > 0 && (
            <>
              <Divider />
              <div className="d-flex justify-content-between align-items-center mb-2">
                <Text strong>Errors ({uploadResults.errors.length})</Text>
                <Button 
                  size="small"
                  icon={<DownloadOutlined />}
                  onClick={downloadErrorReport}
                >
                  Download Error Report
                </Button>
              </div>
              
              <List
                dataSource={uploadResults.errors.slice(0, 10)}
                renderItem={error => (
                  <List.Item>
                    <List.Item.Meta
                      avatar={<ExclamationCircleOutlined style={{ color: '#ff4d4f' }} />}
                      title={error.row ? `Row ${error.row}` : error.siteId || 'Unknown'}
                      description={error.error || error.message}
                    />
                  </List.Item>
                )}
                size="small"
              />
              
              {uploadResults.errors.length > 10 && (
                <Text type="secondary">
                  Showing first 10 errors. Download the full error report for details.
                </Text>
              )}
            </>
          )}
        </Card>
      )}
    </div>
  );
};

export default SiteUpload; 